import React, { useState, useEffect } from 'react';
import { 
  hasConsentChoice, 
  acceptAllCookies, 
  rejectAllCookies, 
  setCookieConsent,
  COOKIE_CATEGORIES 
} from '../utils/cookieConsent';
import { getTranslation } from '../translations';
import { getInitialLanguage } from '../utils/languageDetection';
import './CookieBanner.css';

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [language, setLanguage] = useState(getInitialLanguage());
  const [preferences, setPreferences] = useState({
    [COOKIE_CATEGORIES.STRICTLY_NECESSARY]: true, // Always true
    [COOKIE_CATEGORIES.FUNCTIONAL]: true,
    [COOKIE_CATEGORIES.ANALYTICS]: false,
    [COOKIE_CATEGORIES.MARKETING]: false
  });

  const t = (key) => getTranslation(language, key);

  useEffect(() => {
    // Check if user has already made a choice
    if (!hasConsentChoice()) {
      setIsVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    acceptAllCookies();
    setIsVisible(false);
    window.location.reload(); // Reload to apply consent
  };

  const handleRejectAll = () => {
    rejectAllCookies();
    setIsVisible(false);
    window.location.reload(); // Reload to apply consent
  };

  const handleSavePreferences = () => {
    setCookieConsent(preferences);
    setShowSettings(false);
    setIsVisible(false);
    window.location.reload(); // Reload to apply consent
  };

  const handleTogglePreference = (category) => {
    // Strictly necessary cannot be toggled
    if (category === COOKIE_CATEGORIES.STRICTLY_NECESSARY) return;
    
    setPreferences({
      ...preferences,
      [category]: !preferences[category]
    });
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Cookie Banner */}
      <div className="cookie-banner" role="dialog" aria-label={t('cookie_banner_title')}>
        <div className="cookie-banner-content">
          <div className="cookie-banner-text">
            <h3 className="cookie-banner-title">🍪 {t('cookie_banner_title')}</h3>
            <p className="cookie-banner-description">
              {t('cookie_banner_description')}
            </p>
          </div>
          <div className="cookie-banner-actions">
            <button 
              onClick={() => setShowSettings(true)}
              className="cookie-btn cookie-btn-secondary"
              aria-label={t('cookie_customize')}
            >
              {t('cookie_customize')}
            </button>
            <button 
              onClick={handleRejectAll}
              className="cookie-btn cookie-btn-reject"
              aria-label={t('cookie_reject_all')}
            >
              {t('cookie_reject_all')}
            </button>
            <button 
              onClick={handleAcceptAll}
              className="cookie-btn cookie-btn-accept"
              aria-label={t('cookie_accept_all')}
            >
              {t('cookie_accept_all')}
            </button>
          </div>
        </div>
      </div>

      {/* Cookie Settings Modal */}
      {showSettings && (
        <div className="cookie-modal-overlay" onClick={() => setShowSettings(false)}>
          <div className="cookie-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cookie-modal-header">
              <h2>{t('cookie_settings_title')}</h2>
              <button 
                className="cookie-modal-close"
                onClick={() => setShowSettings(false)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            
            <div className="cookie-modal-body">
              <p className="cookie-modal-intro">
                {t('cookie_settings_intro')}
              </p>

              {/* Strictly Necessary Cookies */}
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <div>
                    <h3>{t('cookie_necessary_title')}</h3>
                    <p className="cookie-category-description">
                      {t('cookie_necessary_description')}
                    </p>
                  </div>
                  <div className="cookie-toggle cookie-toggle-disabled">
                    <input 
                      type="checkbox" 
                      checked={true} 
                      disabled 
                      readOnly
                      aria-label={t('cookie_necessary_title')}
                    />
                    <span className="cookie-toggle-slider"></span>
                  </div>
                </div>
                <p className="cookie-always-active">{t('cookie_always_active')}</p>
              </div>

              {/* Functional Cookies */}
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <div>
                    <h3>{t('cookie_functional_title')}</h3>
                    <p className="cookie-category-description">
                      {t('cookie_functional_description')}
                    </p>
                  </div>
                  <label className="cookie-toggle">
                    <input 
                      type="checkbox" 
                      checked={preferences[COOKIE_CATEGORIES.FUNCTIONAL]}
                      onChange={() => handleTogglePreference(COOKIE_CATEGORIES.FUNCTIONAL)}
                      aria-label={t('cookie_functional_title')}
                    />
                    <span className="cookie-toggle-slider"></span>
                  </label>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <div>
                    <h3>{t('cookie_analytics_title')}</h3>
                    <p className="cookie-category-description">
                      {t('cookie_analytics_description')}
                    </p>
                  </div>
                  <label className="cookie-toggle">
                    <input 
                      type="checkbox" 
                      checked={preferences[COOKIE_CATEGORIES.ANALYTICS]}
                      onChange={() => handleTogglePreference(COOKIE_CATEGORIES.ANALYTICS)}
                      aria-label={t('cookie_analytics_title')}
                    />
                    <span className="cookie-toggle-slider"></span>
                  </label>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="cookie-category">
                <div className="cookie-category-header">
                  <div>
                    <h3>{t('cookie_marketing_title')}</h3>
                    <p className="cookie-category-description">
                      {t('cookie_marketing_description')}
                    </p>
                  </div>
                  <label className="cookie-toggle">
                    <input 
                      type="checkbox" 
                      checked={preferences[COOKIE_CATEGORIES.MARKETING]}
                      onChange={() => handleTogglePreference(COOKIE_CATEGORIES.MARKETING)}
                      aria-label={t('cookie_marketing_title')}
                    />
                    <span className="cookie-toggle-slider"></span>
                  </label>
                </div>
              </div>
            </div>

            <div className="cookie-modal-footer">
              <button 
                onClick={() => setShowSettings(false)}
                className="cookie-btn cookie-btn-secondary"
              >
                {t('cookie_cancel')}
              </button>
              <button 
                onClick={handleSavePreferences}
                className="cookie-btn cookie-btn-accept"
              >
                {t('cookie_save_preferences')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CookieBanner;
